import { useState } from 'react';
import { ArrowLeft, ArrowRight, Check, Users, Calendar, Scale, ListTodo, FileText, ClipboardCheck } from 'lucide-react';
import './styles/PlatformConfiguration.css';

interface Props {
  nextStep: () => void;
  previousStep: () => void;
}

type StyleId = 'overview' | 'detailed' | 'balanced';

interface DashboardStyle {
  id: StyleId;
  name: string;
  badge: string;
  badgeClass: string;
  description: string;
  defaultSections: string[];
}

const STYLES: DashboardStyle[] = [
  {
    id: 'overview',
    name: 'Overview',
    badge: 'Light and Clean',
    badgeClass: 'dw-badge-blue',
    description: 'The most essential governance KPIs and quick-access shortcuts. Ideal for executives who need a high-level summary.',
    defaultSections: ['committees', 'meetings', 'policies'],
  },
  {
    id: 'detailed',
    name: 'Detailed',
    badge: 'Full operational view',
    badgeClass: 'dw-badge-pink',
    description:
      'All KPIs plus recent decisions and policies awaiting action. Ideal for governance managers who need full daily visibility.',
    defaultSections: ['committees', 'meetings', 'decisions', 'actions', 'policies', 'acknowledgements'],
  },
  {
    id: 'balanced',
    name: 'Balanced',
    badge: 'Middle ground',
    badgeClass: 'dw-badge-teal',
    description: 'Key KPIs, a recent decisions feed, and module shortcuts — a comfortable balance between overview and detail.',
    defaultSections: ['committees', 'meetings', 'decisions', 'policies'],
  },
];

interface Section {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
}

const SECTIONS: Section[] = [
  {
    id: 'committees',
    name: 'Committees Summary',
    description: 'Active committees count and direct navigation list',
    icon: <Users size={16} />,
    iconBg: '#f3e8ff',
    iconColor: '#7c3aed',
  },
  {
    id: 'meetings',
    name: 'Meetings Summary',
    description: 'Upcoming scheduled meetings calendar snapshot',
    icon: <Calendar size={16} />,
    iconBg: '#e0f2fe',
    iconColor: '#0284c7',
  },
  {
    id: 'decisions',
    name: 'Decisions Summary',
    description: 'Decisions pending action or ready for formal signatures',
    icon: <Scale size={16} />,
    iconBg: '#fef3c7',
    iconColor: '#d97706',
  },
  {
    id: 'actions',
    name: 'Action Items Summary',
    description: 'Open action items from board and committee meetings',
    icon: <ListTodo size={16} />,
    iconBg: '#e0f2fe',
    iconColor: '#0284c7',
  },
  {
    id: 'policies',
    name: 'Policies Summary',
    description: 'Published policies count and quick links to drafts',
    icon: <FileText size={16} />,
    iconBg: '#d1fae5',
    iconColor: '#059669',
  },
  {
    id: 'acknowledgements',
    name: 'Acknowledgements Summary',
    description: 'Pending staff policy acknowledgements and completion metrics',
    icon: <ClipboardCheck size={16} />,
    iconBg: '#fce7f3',
    iconColor: '#db2777',
  },
];

const OverviewPreview = () => (
  <div className="dw-preview dw-preview-overview">
    <div className="dw-preview-label">GOVERNANCE DASHBOARD</div>
    <div className="dw-preview-kpis">
      <div className="dw-kpi dw-kpi-pink">
        <span className="dw-kpi-label">COMMITTEES</span>
        <span className="dw-kpi-value">4</span>
      </div>
      <div className="dw-kpi dw-kpi-blue">
        <span className="dw-kpi-label">POLICIES</span>
        <span className="dw-kpi-value">12</span>
      </div>
      <div className="dw-kpi dw-kpi-teal">
        <span className="dw-kpi-label">MEETINGS</span>
        <span className="dw-kpi-value">2</span>
      </div>
    </div>
    <div className="dw-preview-shortcuts">
      <span>Company Profile</span>
      <span>Governance</span>
      <span>Policies</span>
    </div>
  </div>
);

const DetailedPreview = () => (
  <div className="dw-preview dw-preview-detailed">
    <div className="dw-preview-label">GOVERNANCE DASHBOARD</div>
    <div className="dw-preview-kpis dw-preview-kpis-grid">
      <div className="dw-kpi dw-kpi-pink">
        <span className="dw-kpi-label">COMMITTEES</span>
        <span className="dw-kpi-value">4</span>
      </div>
      <div className="dw-kpi dw-kpi-teal">
        <span className="dw-kpi-label">MEETINGS</span>
        <span className="dw-kpi-value">2</span>
      </div>
      <div className="dw-kpi dw-kpi-blue">
        <span className="dw-kpi-label">POLICIES</span>
        <span className="dw-kpi-value">12</span>
      </div>
      <div className="dw-kpi dw-kpi-amber">
        <span className="dw-kpi-label">DECISIONS</span>
        <span className="dw-kpi-value">3</span>
      </div>
      <div className="dw-kpi dw-kpi-indigo">
        <span className="dw-kpi-label">ACTIONS</span>
        <span className="dw-kpi-value">7</span>
      </div>
      <div className="dw-kpi dw-kpi-rose">
        <span className="dw-kpi-label">PENDING</span>
        <span className="dw-kpi-value">5</span>
      </div>
    </div>
    <div className="dw-preview-feed">
      <div className="dw-feed-item">
        <span className="dw-feed-dot dw-feed-dot-amber" />
        Recent decision
      </div>
      <div className="dw-feed-item">
        <span className="dw-feed-dot dw-feed-dot-blue" />
        Policy update
      </div>
    </div>
  </div>
);

const BalancedPreview = () => (
  <div className="dw-preview dw-preview-balanced">
    <div className="dw-preview-label">GOVERNANCE DASHBOARD</div>
    <div className="dw-preview-kpis">
      <div className="dw-kpi dw-kpi-pink">
        <span className="dw-kpi-label">COMMITTEES</span>
        <span className="dw-kpi-value">4</span>
      </div>
      <div className="dw-kpi dw-kpi-teal">
        <span className="dw-kpi-label">MEETINGS</span>
        <span className="dw-kpi-value">2</span>
      </div>
      <div className="dw-kpi dw-kpi-amber">
        <span className="dw-kpi-label">DECISIONS</span>
        <span className="dw-kpi-value">3</span>
      </div>
      <div className="dw-kpi dw-kpi-blue">
        <span className="dw-kpi-label">POLICIES</span>
        <span className="dw-kpi-value">12</span>
      </div>
    </div>
    <div className="dw-preview-shortcuts">
      <span>Recent Decisions</span>
      <span>Company Profile</span>
    </div>
  </div>
);

const PREVIEW_MAP: Record<StyleId, React.ReactNode> = {
  overview: <OverviewPreview />,
  detailed: <DetailedPreview />,
  balanced: <BalancedPreview />,
};

const DashboardWidgets = ({ nextStep, previousStep }: Props) => {
  const [selectedStyle, setSelectedStyle] = useState<StyleId>('detailed');
  const [enabledSections, setEnabledSections] = useState<Record<string, boolean>>(
    Object.fromEntries(STYLES.find((s) => s.id === 'detailed')!.defaultSections.map((id) => [id, true])),
  );

  const handleStyleSelect = (style: DashboardStyle) => {
    setSelectedStyle(style.id);
    const next: Record<string, boolean> = {};
    SECTIONS.forEach((sec) => {
      next[sec.id] = style.defaultSections.includes(sec.id);
    });
    setEnabledSections(next);
  };

  const toggleSection = (id: string) => {
    setEnabledSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="dw-card">
      <div className="dw-header">
        <h2 className="dw-title">Dashboard Widgets</h2>
        <p className="dw-subtitle">Choose which summary sections appear on the Governance dashboard</p>
      </div>

      {/* Choose a dashboard style */}
      <div className="dw-section">
        <h3 className="dw-section-title">Choose a dashboard style</h3>
        <p className="dw-section-desc">
          Select how the governance dashboard should look. Each style shows different levels of information density.
        </p>

        <div className="dw-styles">
          {STYLES.map((style) => {
            const isSelected = selectedStyle === style.id;
            return (
              <button
                key={style.id}
                type="button"
                className={`dw-style-card ${isSelected ? 'dw-style-card-active' : ''}`}
                onClick={() => handleStyleSelect(style)}
              >
                <div className="dw-style-preview-wrap">{PREVIEW_MAP[style.id]}</div>
                <div className="dw-style-info">
                  <div className="dw-style-name-row">
                    <span className="dw-style-name">{style.name}</span>
                    <span className={`dw-style-badge ${style.badgeClass}`}>{style.badge}</span>
                  </div>
                  <p className="dw-style-desc">{style.description}</p>
                </div>
                <div className={`dw-style-radio ${isSelected ? 'dw-style-radio-on' : ''}`}>
                  {isSelected && <Check size={12} strokeWidth={3} />}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Fine-tune visible sections */}
      <div className="dw-section">
        <h3 className="dw-section-title">Fine-tune visible sections</h3>
        <p className="dw-section-desc">The selected style preselects a default set. Adjust as needed.</p>

        <div className="dw-sections-list">
          {SECTIONS.map((sec) => {
            const isOn = !!enabledSections[sec.id];
            return (
              <button
                key={sec.id}
                type="button"
                className={`dw-section-item ${isOn ? 'dw-section-item-active' : ''}`}
                onClick={() => toggleSection(sec.id)}
              >
                <div className="dw-section-item-left">
                  <div
                    className="dw-section-icon"
                    style={{
                      backgroundColor: sec.iconBg,
                      color: sec.iconColor,
                    }}
                  >
                    {sec.icon}
                  </div>
                  <div className="dw-section-item-text">
                    <span className="dw-section-item-name">{sec.name}</span>
                    <span className="dw-section-item-desc">{sec.description}</span>
                  </div>
                </div>
                <div className={`dw-section-check ${isOn ? 'dw-section-check-on' : 'dw-section-check-off'}`}>
                  {isOn && <Check size={14} strokeWidth={3} />}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="dw-footer">
        <button type="button" onClick={previousStep} className="dw-back-btn">
          <ArrowLeft size={16} />
          Back
        </button>
        <button type="button" onClick={nextStep} className="dw-next-btn">
          Next
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default DashboardWidgets;
